import React, { useEffect, useState } from 'react';
import {
  Box, Alert, Table, TableHead, TableBody, TableRow, TableCell, Switch, Chip, CircularProgress,
} from '@mui/material';
import api from '../../services/api';

// Direct Media (RTP bypass) per (customer × carrier). Writes sbc_media_bypass,
// which the SBC reads live per call — effective on the next call, no restart.
export const DirectMediaTab: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  const load = () => {
    setLoading(true);
    api.get(`/tenants/${tenantId}/media-bypass`)
      .then(({ data }) => setRows(data || []))
      .catch((e) => setErr(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tenantId]);

  const toggle = async (carrier: any, enabled: boolean) => {
    setBusy(carrier.id); setErr('');
    setRows((rs) => rs.map((r) => (r.id === carrier.id ? { ...r, bypass_enabled: enabled } : r))); // optimistic
    try {
      await api.put(`/tenants/${tenantId}/media-bypass/${carrier.id}`, { enabled });
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Update failed');
      setRows((rs) => rs.map((r) => (r.id === carrier.id ? { ...r, bypass_enabled: !enabled } : r))); // revert
    } finally { setBusy(''); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <b>Direct Media (RTP bypass).</b> When ON, RTP for this customer's calls to that carrier flows
        <b> directly customer&nbsp;↔&nbsp;carrier</b> — our SBC keeps only the signalling.
        <b> Prerequisites:</b> the carrier must accept media from this customer's IP, and both legs must be plain RTP.
        Misuse can cause one-way / no audio. Takes effect on the next call (no restart); switch off to instantly re-anchor.
      </Alert>
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Carrier', 'Host', 'Media', 'Direct Media (RTP bypass)'].map((h) =>
              <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell><b>{r.name}</b></TableCell>
              <TableCell sx={{ fontFamily: 'monospace' }}>{r.host}</TableCell>
              <TableCell>{r.srtp && r.srtp !== 'none' ? 'SRTP' : 'Plain RTP'}</TableCell>
              <TableCell>
                <Switch checked={!!r.bypass_enabled} disabled={busy === r.id}
                  onChange={(e) => toggle(r, e.target.checked)} />
                {r.bypass_enabled
                  ? <Chip size="small" color="primary" label="DIRECT" />
                  : <Chip size="small" variant="outlined" label="anchored" />}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
              No termination carriers.
            </TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};
